import React from 'react';
import { Input, Select, H2, Contact, InternationalContact, Icon } from '@tviklax/ui-components';
import { Section, FormGrid, FormGroup, AddressCard, Label } from './styles';
import { OrderFormData } from './types';
import { AddressAutocomplete, AddressAutofillFields } from './AddressAutocomplete';
import { useCountries, useStatesForCountry } from './addressData';

interface CustomerInformationProps {
  formData: OrderFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhoneChange: (value: string) => void;
  onEmailValidate?: (isValid: boolean) => void;
  onPhoneValidate?: (isValid: boolean) => void;
  onAddressAutofill: (fields: Partial<OrderFormData>) => void;
}

export const CustomerInformation: React.FC<CustomerInformationProps> = ({
  formData,
  onChange,
  onEmailChange,
  onPhoneChange,
  onEmailValidate,
  onPhoneValidate,
  onAddressAutofill,
}) => {
  const countries = useCountries();
  const stateOptions = useStatesForCountry(formData.customerCountry);

  const handleCustomerAddressSelected = (fields: AddressAutofillFields) => {
    onAddressAutofill({
      customerAddr1: fields.street,
      customerCity: fields.city,
      customerState: fields.state,
      customerZip: fields.postalCode,
      customerCountry: fields.country,
    });
  };

  return (
    <Section>
      <H2 icon={<Icon name="User" size={20} color="#f86400" />}>Customer Information</H2>
      <FormGrid style={{ marginBottom: '24px' }}>
        <FormGroup $span={6}>
          <Input
            label="Customer Name"
            type="text"
            name="CustomerName"
            value={formData.CustomerName}
            onChange={onChange}
            placeholder="Enter full legal name"
            required
          />
        </FormGroup>
        <FormGroup $span={3}>
          <Contact
            variant="email"
            value={formData.CustomerEmail}
            onChange={onEmailChange}
            onValidate={onEmailValidate}
            label="Email Address"
            required
          />
        </FormGroup>
        <FormGroup $span={3}>
          <InternationalContact
            variant="cell"
            value={formData.CustomerContactNumber}
            onChange={onPhoneChange}
            onValidate={onPhoneValidate}
            defaultCountry={formData.customerCountry || 'IN'}
            label="Contact Number"
            required
          />
        </FormGroup>
      </FormGrid>

      <Label style={{ marginBottom: '8px', display: 'block' }}>Customer Address</Label>
      <AddressCard>
        <FormGroup $span={6}>
          <AddressAutocomplete
            label="Street Address"
            name="customerAddr1"
            value={formData.customerAddr1}
            onChange={onChange}
            onAddressSelected={handleCustomerAddressSelected}
            placeholder="e.g., 123 Business Way"
            required
          />
        </FormGroup>
        <FormGroup $span={3}>
          <Input
            label="Apt / Suite"
            name="customerAddr2"
            value={formData.customerAddr2}
            onChange={onChange}
            placeholder="Suite 100"
          />
        </FormGroup>
        <FormGroup $span={3}>
          <Input
            label="Zip Code"
            name="customerZip"
            value={formData.customerZip}
            onChange={onChange}
            placeholder="Zip Code"
            required
          />
        </FormGroup>
        <FormGroup $span={2}>
          <Input
            label="City"
            name="customerCity"
            value={formData.customerCity}
            onChange={onChange}
            placeholder="City"
            required
          />
        </FormGroup>
        <FormGroup $span={2}>
          <Select
            label="State"
            name="customerState"
            value={formData.customerState}
            onChange={onChange}
            items={stateOptions}
            placeholder="Select State"
            disabled={stateOptions.length === 0}
            required={stateOptions.length > 0}
          />
        </FormGroup>
        <FormGroup $span={2}>
          <Select
            label="Country"
            name="customerCountry"
            value={formData.customerCountry}
            onChange={onChange}
            items={countries}
            placeholder="Select Country"
            required
          />
        </FormGroup>
      </AddressCard>
    </Section>
  );
};
